import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { PersonApiService } from '@atmi/core';
import { makeQueryBuilder, makeQueryRules } from '@ui/ui.module';

import { PersonFilterMeta } from '@sim/person/models/person';

@Component({
  providers: [{
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputPersonAutocompleteComponent),
  }],
  selector: 'app-input-person-autocomplete',
  styleUrls: ['./input-person-autocomplete.component.scss'],
  templateUrl: './input-person-autocomplete.component.html',
})
export class InputPersonAutocompleteComponent implements ControlValueAccessor {

  /** Internal value used to contain the current input selection. */
  readonly value$ = new BehaviorSubject<string | undefined>(undefined);
  /** Internal extended value if the list of items is a complex object */
  extendedValue?: { id: string };
  /** Internal disable state used to disable/enable the ng-select component. */
  readonly isDisabled$ = new BehaviorSubject(false);
  /** Internal state used to check if ng-select has been focus. */
  readonly isOnFocus$ = new BehaviorSubject(false);

  @Input() bindLabel = 'completeName';
  @Input() bindValue = 'id';

  @Input() placeholder = 'Fornitore';

  readonly peopleInput$ = new BehaviorSubject('');

  readonly people$ = combineLatest([
    this.peopleInput$,
    this.value$,
  ]).pipe(
    switchMap(([ragioneSociale, id]) => {
      const rules = id
        ? makeQueryRules({ id }, PersonFilterMeta)
        : makeQueryRules({ ragioneSociale, tipo: 'Fornitore' }, PersonFilterMeta);
      const query = makeQueryBuilder(rules, 'OR');

      return this.personApi.index({ limit: 100, page: 0, filter: query });
    }),
    map((page) => page.data.map((d) => ({
      completeName: d.ragioneSociale,
      id: d.id,
    })))
  );

  private onChange!: (_: any) => void;
  private onTouched!: () => void;

  constructor(
    private readonly personApi: PersonApiService,
  ) {
  }

  writeValue(v?: string) {
    this.value$.next(v);
  }

  registerOnChange(fn: (value: any) => any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled$.next(isDisabled);
  }

  changeValueHandler(value: { id: string } | undefined) {
    this.extendedValue = value;

    this.value$.next(value && value.id);

    this.onChange(value && value.id);
  }

  onFocusHandler() {
    this.isOnFocus$.next(true);
  }
}
